/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import apiClient from '../api/client';

/*
 * Apre un PDF generato dal backend in una scheda nuova.
 *
 * Il PDF passa da una rotta autenticata, quindi un semplice link non basta (non
 * porterebbe il token): si scarica come blob e si apre un URL locale. La scheda
 * si apre SUBITO, al clic, e si riempie dopo: aperta a download finito, il
 * browser la tratterebbe come un popup e la bloccherebbe.
 */
async function apriPdf(percorso, params, nomeFile) {
  const scheda = window.open('', '_blank');
  if (scheda) {
    scheda.document.title = 'Preparazione del PDF...';
    scheda.document.body.style.fontFamily = 'system-ui, sans-serif';
    scheda.document.body.innerText = 'Preparazione del PDF in corso...';
  }
  try {
    const { data } = await apiClient.get(percorso, { params, responseType: 'blob', timeout: 120000 });
    const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
    if (scheda) {
      scheda.location.href = url;
    } else {
      // Popup bloccato comunque: si scarica il file.
      const a = document.createElement('a');
      a.href = url;
      a.download = nomeFile;
      a.click();
    }
    // L'URL resta valido il tempo di aprirlo; poi si libera la memoria.
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    if (scheda) scheda.close();
    let msg = 'Non sono riuscito a generare il PDF.';
    // Con responseType blob anche l'errore arriva come blob: va letto.
    try { msg = JSON.parse(await e.response.data.text()).error || msg; } catch { /* resta il messaggio generico */ }
    throw new Error(msg);
  }
}

const unoZero = (v, predefinito) => ((v === undefined ? predefinito : v) ? 1 : 0);

/*
 * Il resoconto di un intervento.
 * opzioni: { importi, foto, interno } — vedi backend/src/routes/resoconto.js
 */
export function apriResoconto(interventoId, codice, opzioni = {}) {
  return apriPdf(`/interventi/${interventoId}/resoconto`, {
    importi: unoZero(opzioni.importi, true),
    foto: unoZero(opzioni.foto, true),
    interno: unoZero(opzioni.interno, false),
  }, `Resoconto-${codice || interventoId}.pdf`);
}

/*
 * Il registro dei lavori di un periodo.
 * filtri: { dal, al, tecnico, cliente, intervento, nascosti: [], materiali, importi, interno }
 * — vedi backend/src/routes/registroLavori.js
 */
export function apriRegistro(filtri) {
  const params = {
    dal: filtri.dal,
    al: filtri.al,
    materiali: unoZero(filtri.materiali, false),
    importi: unoZero(filtri.importi, false),
    interno: unoZero(filtri.interno, false),
  };
  if (filtri.tecnico) params.tecnico = filtri.tecnico;
  if (filtri.cliente) params.cliente = filtri.cliente;
  if (filtri.intervento) params.intervento = filtri.intervento;
  if (filtri.nascosti && filtri.nascosti.length) params.nascosti = filtri.nascosti.join(',');
  return apriPdf('/registro-lavori', params, `Registro-lavori-${filtri.dal}_${filtri.al}.pdf`);
}
